<template>
  <div>
    <div class="group-content d-flex justify-space-between">
      <div class="d-flex align-start">
        <mf-upload-image
          v-if="group.group_rules_exhibition === 'PUBLIC'"
          v-model="group.group_image_url"
          dense
          :file-name="`${group.name}-image`"
          :loading="loading"
          :disabled="isRevision || disabled || isIndustryCampaign"
          :disabled-remove="isRevision || disabled"
          @snackbar-error="showSnackbarError"
          @upload="saveImage"
        />
        <div class="group-content-info ml-3">
          <h2 class="card-title ma-0 pa-0">{{ group.name }}</h2>
          <p class="texto-corrido mt-2">
            Um grupo <b>público</b> é exibido para o consumidor final como <b>uma única oferta</b>, utilizando imagem e descrição do grupo, enquanto um grupo
            <b>interno</b> é exibido como <b>ofertas separadas</b> no aplicativo, utilizando imagens e descrições dos produtos individualmente.
          </p>
          <div class="btn-group pb-3">
            <mf-button
              :disabled="isRevision || disabled || isIndustryCampaign"
              :outlined="group.group_rules_exhibition === 'PUBLIC'"
              size="sm"
              :color="group.group_rules_exhibition === 'PRIVATE' ? 'marca-0-l10' : 'cinza-0'"
              label="Interno"
              @click="changeGroupExhibition('PRIVATE')"
            />
            <mf-tooltip :disabled="!disableChangeTypeButtonIfIsTakePay || isRevision || disabled" top max-width="250px">
              <template #activator="{ on }">
                <mf-button
                  :disabled="disableChangeTypeButtonIfIsTakePay || isRevision || disabled || isIndustryCampaign || isOffersByUnit"
                  :outlined="group.group_rules_exhibition === 'PRIVATE'"
                  size="sm"
                  :color="group.group_rules_exhibition === 'PUBLIC' ? 'marca-0-l10' : 'cinza-0'"
                  label="Público"
                  @click="changeGroupExhibition('PUBLIC')"
                />
              </template>
              <span v-if="isOffersByUnit">Não é possível utilizar um grupo público neste tipo de oferta.</span>
              <span v-else
                >Tudo que é cadastrado em um grupo público é exibido para seu consumidor final. Ainda não é possível compartilhar minimos e máximos entre
                produtos, apesar de serem de um mesmo grupo. Por isso não é possível usar um grupo público nesse tipo de oferta ainda.</span
              >
            </mf-tooltip>
          </div>
        </div>
      </div>
      <mf-button
        v-if="!isRevision"
        text
        color="vermelho-d20"
        :disabled="disableRemoveButton || disabled || isIndustryCampaign"
        class="unset-capitalize"
        label="Remover grupo"
        icon="delete"
        @click="$emit('remove-group')"
      />
    </div>
    <mf-info-card v-bind="groupInfoProps" :v-html="groupInfoVHTML" />
    <mf-info-card
      v-if="isIndustryCampaign && (offerSalesType === 'takepay_offer_sales' || offerSalesType === 'percentage_offer_sales')"
      class="mt-2"
      variant="success"
      :v-html="industryRebateWarning"
      icon="payments"
    />
    <component
      :is="getComponent"
      class="mt-4"
      type="grouped"
      :products="productsInsideGroup"
      :headers="headers"
      :helpers="helpers"
      :group="group"
      :repeated-skus="repeatedSkus"
      :validations="validations"
      :group-index="groupIndex"
      :search="search"
      :disable-min-and-max-cpf="disableMinAndMaxCpf"
      :is-revision="isRevision"
      :offer-sales-type="offerSalesType"
      :disabled="disabled"
      :is-disabled-in-rules="isDisabledInRules"
      :is-industry-campaign="isIndustryCampaign"
      :tooltip-message="tooltipMessage"
      :campaign="campaign"
      @remove-item="$emit('remove-item', $event)"
    />
  </div>
</template>

<script>
import AbsoluteDiscountTable from './absolute-discount/AbsoluteDiscountTable.vue'
import PercentageDiscountTable from './percentage-discount/PercentageDiscountTable.vue'
import DynamicPricingPercentageDiscountTable from './dynamic-pricing-percentage-discount/DynamicPricingPercentageDiscountTable.vue'
import CashbackAndPointsTable from './cashback-points/CashbackAndPointsTable.vue'
import TakePayTable from './take-pay/TakePayTable.vue'

import { MUTATION_SINGLE_UPLOAD_BASE64 } from '@/graphql'
export default {
  name: 'GroupCard',
  components: {
    AbsoluteDiscountTable,
    PercentageDiscountTable,
    DynamicPricingPercentageDiscountTable,
    CashbackAndPointsTable,
    TakePayTable,
  },
  props: {
    group: {
      type: Object,
      default: () => {},
    },
    headers: {
      type: Array,
      default: () => [],
    },
    helpers: {
      type: Object,
      default: () => {},
    },
    offerSalesType: {
      type: String,
      default: '',
    },
    repeatedSkus: {
      type: Array,
      default: () => [],
    },
    validations: {
      type: Object,
      default: () => {},
    },
    groupIndex: {
      type: Number,
      default: 0,
    },
    search: {
      type: String,
      default: '',
    },
    isRevision: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isDisabledInRules: {
      type: Function,
      default: () => false,
    },
    isIndustryCampaign: {
      type: Boolean,
      default: false,
    },
    tooltipMessage: {
      type: String,
      default: '',
    },
    campaign: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    panel: false,
    loading: false,
  }),
  computed: {
    isOffersByUnit() {
      return this.$getAllowedModules()['offers_by_unit'] && this.offerSalesType === 'dynamic_pricing_percentage_offer_sales'
    },
    groupInfoVHTML() {
      if (this.group.group_rules_exhibition === 'PRIVATE') {
        if (this.offerSalesType === 'takepay_offer_sales') {
          return '<span style="color: #B3280D">Utilizar grupos de produtos como <b>internos</b> faz com que as quantidades utilizada para calcular o "leve" e o "pague" <b>não sejam compartilhados</b> entre os produtos. Para produtos que devem contribuir em conjunto para o "leve" e o "pague", como múltiplos sabores, altere o grupo para <b>público</b>.</span>'
        } else {
          return '<span style="color: #B3280D">Utilizar grupos de produtos como <b>internos</b> faz com que mínimos e máximos <b>não sejam compartilhados</b> entre os produtos. Para ofertas que devem compartilhar mínimo e máximo, como múltiplos sabores, altere o grupo para <b>público</b>.</span>'
        }
      } else {
        if (this.offerSalesType === 'takepay_offer_sales') {
          return '<span style="color: #004D99">Utilizar grupos de produtos como <b>públicos</b> faz com que as quantidades utilizadas para calcular o "leve" e o "pague" <b>sejam compartilhados</b> entre os produtos, o que é indicado para ofertas como produtos com múltiplos sabores.</span>'
        } else {
          return '<span style="color: #004D99">Utilizar grupos de produtos como <b>públicos</b> faz com que mínimos e máximos <b>sejam compartilhados</b> entre os produtos, o que é indicado para ofertas como produtos com múltiplos sabores.</span>'
        }
      }
    },
    industryRebateWarning() {
      return '<span style="color: #086A29">Todo o valor do desconto concedido para o consumidor através desta oferta será reembolsado pela indústria. Portanto essa oferta não gera diminuição da margem do produto.</span>'
    },
    groupInfoProps() {
      return {
        variant: this.group.group_rules_exhibition === 'PRIVATE' ? 'error' : 'info',
        icon: this.group.group_rules_exhibition === 'PRIVATE' ? 'error' : 'info',
      }
    },
    productsInsideGroup() {
      return this.group.products
    },
    disableMinAndMaxCpf() {
      return (
        (this.offerSalesType === 'offer_sales' ||
          this.offerSalesType === 'percentage_offer_sales' ||
          this.offerSalesType === 'dynamic_pricing_percentage_offer_sales') &&
        this.group_rules_exhibition === 'PUBLIC'
      )
    },
    disableChangeTypeButtonIfIsTakePay() {
      if (this.isOffersByUnit) return true
      if (this.$getAllowedModules()['jane']) return false
      return this.offerSalesType === 'takepay_offer_sales'
    },
    disableRemoveButton() {
      return this.productsInsideGroup.some((product) => product.disabled)
    },
    groupHasRepeatedSku() {
      if (this.repeatedSkus.length === 0) return false

      let groupRepeatedSkus = []
      this.productsInsideGroup.forEach((product) => {
        if (this.repeatedSkus.includes(product.sku)) groupRepeatedSkus.push(product.sku)
      })

      return groupRepeatedSkus.length > 0
    },
    getComponent() {
      const components = {
        offer_sales: AbsoluteDiscountTable,
        percentage_offer_sales: PercentageDiscountTable,
        dynamic_pricing_percentage_offer_sales: DynamicPricingPercentageDiscountTable,
        takepay_offer_sales: TakePayTable,
        cashback_percentage_offer_sales: CashbackAndPointsTable,
        cashback_absolute_offer_sales: CashbackAndPointsTable,
        points_offer_sales: CashbackAndPointsTable,
        next_buy_cashback_offer_sales: CashbackAndPointsTable,
      }

      return components[this.offerSalesType]
    },
  },
  methods: {
    showSnackbarError(snackbar) {
      this.$snackbar(snackbar)
    },
    changeGroupExhibition(type) {
      this.$emit('change-group-exhibition', type)
      if (this.disableMinAndMaxCpf) {
        this.group.maximum_per_client = 0
        this.group.minimum_per_client = 0
      }
    },
    async saveImage({ base64, filename }) {
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: MUTATION_SINGLE_UPLOAD_BASE64,
          variables: { base64, filename },
          context: {
            uri: this.$microservicesUrls['upload'],
          },
        })
        .then(({ data: { singleUploadBase64 } }) => {
          const { url } = singleUploadBase64
          this.group.group_image_url = url
          this.loading = false
        })
        .catch(() => {
          this.loading = false
          this.$snackbar({ message: 'Erro ao subir imagem.', snackbarColor: 'error' })
        })
    },
  },
}
</script>

<style scoped lang="scss">
.group-content {
  padding: 20px 0;
}

.group-content-info {
  max-width: 760px;
}
.btn-group {
  & > button {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  & > span > button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.unset-capitalize {
  text-transform: unset !important;
  letter-spacing: $letterspacing-1;
}
</style>
